import React, { useEffect, useState } from 'react'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/Button'
import { Plus, Trash2, Edit2, Users } from 'lucide-react'
import toast from 'react-hot-toast'

interface Team { id: number; name: string }
interface Member { id: number; name: string; email: string; role: string }

const TeamsManagement: React.FC = () => {
  const [teams, setTeams] = useState<Team[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [members, setMembers] = useState<Member[]>([])
  const [assignUserId, setAssignUserId] = useState<number | ''>('')
  const [assigning, setAssigning] = useState(false)
  const [availableUsers, setAvailableUsers] = useState<Member[]>([])

  const fetchTeams = async () => {
    setIsLoading(true)
    try {
      const res = await apiClient.get('/api/v1/admin/teams')
      setTeams(res.data?.teams || [])
    } catch (e) {
      toast.error('Takımlar getirilemedi')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchMembers = async (team: Team) => {
    try {
      const res = await apiClient.get('/api/v1/users', { params: { is_active: true, page_size: 100, team_id: team.id } })
      const list: Member[] = (res.data?.users || []).map((u: any) => ({ id: u.id, name: `${u.first_name} ${u.last_name}`, email: u.email, role: u.role }))
      setMembers(list)
    } catch { setMembers([]) }
  }

  const fetchAssignable = async () => {
    try {
      const [staff, cca] = await Promise.all([
        apiClient.get('/api/v1/users', { params: { role: 'staff', is_active: true, page_size: 100 } }),
        apiClient.get('/api/v1/users', { params: { role: 'callcenter_admin', is_active: true, page_size: 100 } }),
      ])
      const merged = [...(staff.data?.users || []), ...(cca.data?.users || [])].map((u: any) => ({ id: u.id, name: `${u.first_name} ${u.last_name}`, email: u.email, role: u.role }))
      setAvailableUsers(merged)
    } catch { setAvailableUsers([]) }
  }

  useEffect(() => { fetchTeams(); fetchAssignable() }, [])

  const createTeam = async () => {
    if (!newTeamName.trim()) return
    try {
      await apiClient.post('/api/v1/admin/teams', null, { params: { name: newTeamName.trim() } })
      toast.success('Takım oluşturuldu')
      setNewTeamName('')
      fetchTeams()
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Oluşturulamadı') }
  }

  const saveTeam = async (team: Team) => {
    try {
      await apiClient.put(`/api/v1/admin/teams/${team.id}`, null, { params: { name: editingName.trim() } })
      toast.success('Takım güncellendi')
      setEditingId(null)
      setEditingName('')
      fetchTeams()
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Güncellenemedi') }
  }

  const deleteTeam = async (team: Team) => {
    if (!confirm(`${team.name} silinsin mi?`)) return
    try {
      await apiClient.delete(`/api/v1/admin/teams/${team.id}`)
      toast.success('Takım silindi')
      setSelectedTeam(null)
      setMembers([])
      fetchTeams()
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Silinemedi') }
  }

  const assignUser = async () => {
    if (!selectedTeam || !assignUserId) return
    setAssigning(true)
    try {
      await apiClient.put(`/api/v1/users/${assignUserId}`, { team_id: selectedTeam.id })
      toast.success('Kullanıcı takıma atandı')
      fetchMembers(selectedTeam)
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Atanamadı') }
    finally { setAssigning(false); setAssignUserId('') }
  }

  const inputCls = "px-3 py-2 border border-slate-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4 pb-5 border-b border-slate-200">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-1">Yönetim</p>
          <h1 className="text-2xl font-serif font-medium text-slate-900 flex items-center gap-2"><Users className="w-6 h-6 text-primary-600"/> Takım Yönetimi</h1>
        </div>
        <div className="flex gap-2">
          <input value={newTeamName} onChange={(e)=>setNewTeamName(e.target.value)} placeholder="Yeni takım adı" className={inputCls}/>
          <Button onClick={createTeam}><Plus className="w-4 h-4 mr-1"/>Ekle</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
          {isLoading ? (
            <p className="text-slate-600">Yükleniyor...</p>
          ) : teams.length === 0 ? (
            <p className="text-slate-600">Henüz takım yok.</p>
          ) : (
            <div className="space-y-2">
              {teams.map(team => (
                <div key={team.id} className={`p-3 border flex items-center justify-between ${selectedTeam?.id === team.id ? 'bg-primary-50 border-primary-200' : 'border-slate-200'}`}>
                  {editingId === team.id ? (
                    <div className="flex-1 flex items-center gap-2">
                      <input value={editingName} onChange={(e)=>setEditingName(e.target.value)} className={inputCls + " w-full"}/>
                      <Button onClick={()=>saveTeam(team)}>Kaydet</Button>
                      <Button variant="outline" onClick={()=>{setEditingId(null); setEditingName('')}}>İptal</Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{team.name}</p>
                        <p className="text-xs text-slate-500">Üye sayısı: {team.id === selectedTeam?.id ? members.length : '-'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={()=>{setSelectedTeam(team); fetchMembers(team)}}>Seç</Button>
                        <Button variant="outline" onClick={()=>{setEditingId(team.id); setEditingName(team.name)}}><Edit2 className="w-4 h-4"/></Button>
                        <Button variant="outline" className="text-danger-600" onClick={()=>deleteTeam(team)}><Trash2 className="w-4 h-4"/></Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 border-t-2 border-t-gold-500 p-6">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">Takım Üyeleri</h2>
          {!selectedTeam ? (
            <p className="text-slate-600">Sol taraftan bir takım seçin.</p>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-4">
                <select value={assignUserId} onChange={(e)=>setAssignUserId(Number(e.target.value))} className={inputCls + " flex-1"}>
                  <option value="">Kullanıcı seç (personel + callcenter admin)</option>
                  {availableUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
                <Button onClick={assignUser} disabled={assigning || !assignUserId}>Ata</Button>
              </div>
              {members.length === 0 ? (
                <p className="text-slate-600">Bu takımda henüz üye yok.</p>
              ) : (
                <div className="space-y-2">
                  {members.map(m => (
                    <div key={m.id} className="p-3 border border-slate-200 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{m.name}</p>
                        <p className="text-xs text-slate-500">{m.email} • {m.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default TeamsManagement
